package com.qrkontaq.ui.admin

import com.qrkontaq.core.auth.Session
import com.qrkontaq.core.config.AppConfig
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlin.js.Promise

/**
 * Registers the service worker and subscribes this device to Web Push so the
 * waiter/cook get OS notifications even when the app is closed or the phone is
 * locked. All best-effort: unsupported browsers or denied permission are no-ops.
 */
object WebPush {
    private var done = false

    suspend fun enable(rid: String) {
        if (done) return
        try {
            val nav = window.navigator.asDynamic()
            val notif = window.asDynamic().Notification
            if (nav.serviceWorker == null || window.asDynamic().PushManager == null || notif == null) return

            when (notif.permission) {
                "granted" -> Unit
                "denied" -> return
                else -> {
                    val perm = (notif.requestPermission() as Promise<String>).await()
                    if (perm != "granted") return
                }
            }

            nav.serviceWorker.register("/service-worker.js").unsafeCast<Promise<dynamic>>().await()
            val reg = nav.serviceWorker.ready.unsafeCast<Promise<dynamic>>().await()

            var sub = reg.pushManager.getSubscription().unsafeCast<Promise<dynamic>>().await()
            if (sub == null) {
                val vapid = fetchVapidKey() ?: return
                val opts: dynamic = js("({})")
                opts.userVisibleOnly = true
                opts.applicationServerKey = urlB64ToUint8(vapid)
                sub = reg.pushManager.subscribe(opts).unsafeCast<Promise<dynamic>>().await()
            }
            postSubscription(rid, sub)
            done = true
        } catch (_: Throwable) {
        }
    }

    private suspend fun fetchVapidKey(): String? {
        return try {
            val resp = window.asDynamic().fetch(AppConfig.getApiUrl("v1/push/vapid-public-key")).unsafeCast<Promise<dynamic>>().await()
            val json = resp.json().unsafeCast<Promise<dynamic>>().await()
            (json.data?.publicKey as? String)?.takeIf { it.isNotBlank() }
        } catch (_: Throwable) {
            null
        }
    }

    private suspend fun postSubscription(
        rid: String,
        sub: dynamic,
    ) {
        val j = sub.toJSON()
        val body: dynamic = js("({ keys: {} })")
        body.endpoint = j.endpoint
        body.keys.p256dh = j.keys.p256dh
        body.keys.auth = j.keys.auth

        val opts: dynamic = js("({ headers: {} })")
        opts.method = "POST"
        opts.headers["Content-Type"] = "application/json"
        opts.headers["Authorization"] = "Bearer ${Session.accessToken.orEmpty()}"
        opts.body = kotlin.js.JSON.stringify(body)

        window.asDynamic()
            .fetch(AppConfig.getApiUrl("v1/admin/restaurants/$rid/push/subscribe"), opts)
            .unsafeCast<Promise<dynamic>>().await()
    }

    /** VAPID public key (base64url) → Uint8Array for applicationServerKey. */
    private fun urlB64ToUint8(base64: String): dynamic {
        val padding = "=".repeat((4 - base64.length % 4) % 4)
        val b64 = (base64 + padding).replace('-', '+').replace('_', '/')
        val raw = window.atob(b64)
        val arr = js("new Uint8Array(raw.length)")
        for (i in 0 until raw.length) {
            arr[i] = raw.asDynamic().charCodeAt(i)
        }
        return arr
    }
}
